// Step-up (re-verify identity before high-privilege actions) is wired to
// Clerk's reverification flow. `useReverification` from `@clerk/nextjs`
// wraps an arbitrary fetcher: when the fetcher returns Clerk's standard
// reverification "hint" object, the hook opens Clerk's hosted
// reverification modal, waits for it to complete, and re-invokes the
// fetcher exactly once. On modal cancellation it throws a
// `ReverificationCancelledError` (re-exported as `StepUpCancelledError`).
//
// Server side: a route guarded by `withStepUp()` returns 401 with code
// `admin/requires_step_up` when the Clerk session is outside its
// step-up window (see `identity/service/load-session.ts`). The
// `requireStepUp` helper here adapts that platform error code into the
// shape `useReverification` watches for.

import { useCallback } from 'react';
import { useReverification } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';

import { failureFromError, type FailureResult } from './api-error';

export const STEP_UP_ERROR_CODE = 'admin/requires_step_up' as const;

export class StepUpCancelledError extends Error {
  constructor() {
    super('step-up cancelled');
    this.name = 'StepUpCancelledError';
  }
}

// Server-action result shape consumed by step-up call sites. Action
// failures may carry a `code`; when that code is the platform's
// step-up error, `requireStepUp` triggers Clerk's reverification UI
// and retries the action once on success.
type ActionResult = { ok: true; [k: string]: unknown } | {
  ok: false;
  code?: string;
  message?: string;
  [k: string]: unknown;
};

// Clerk's reverification "hint" — the exact object shape
// `useReverification` watches for in fetcher return values. Importing
// the runtime helper from `@clerk/shared` would couple us to a
// non-public surface; the shape itself is part of Clerk's documented
// public contract (see clerk.com/docs/guides/secure/reverification).
const REVERIFICATION_HINT = {
  clerk_error: {
    type: 'forbidden' as const,
    reason: 'reverification-error' as const,
  },
} as const;

type ReverificationHint = typeof REVERIFICATION_HINT;

// Outcome of running a mutation through step-up. `cancelled` covers both
// the user dismissing Clerk's reverification modal and the (defensive)
// case where the action still reports step-up after a retry — in both,
// the caller leaves state untouched and shows nothing. `error` carries a
// non-step-up failure for the caller to surface; `ok` carries the
// mutation's own return value.
export type StepUpOutcome<T> =
  | { readonly status: 'ok'; readonly data: T }
  | { readonly status: 'cancelled' }
  | { readonly status: 'error'; readonly code: string; readonly message: string };

export interface UseStepUpResult {
  readonly requireStepUp: <T extends ActionResult>(
    action: () => Promise<T>,
  ) => Promise<T | undefined>;
  // Higher-level wrapper around `requireStepUp` for the common case: run a
  // mutation, auto-trigger reverification when the server demands step-up,
  // and normalise every result into a `StepUpOutcome` the caller switches
  // on — without re-implementing Clerk cancellation + error extraction at
  // each call site. This is the canonical entry point for step-up'd admin
  // actions; reach for the lower-level `requireStepUp` only when you need
  // the raw action-result shape.
  readonly runStepUp: <T>(
    mutate: () => Promise<T>,
    fallbackMessage: string,
  ) => Promise<StepUpOutcome<T>>;
}

export function useStepUp(): UseStepUpResult {
  // The fetcher runs the caller's action. If the server says
  // step-up is required, the fetcher returns Clerk's reverification
  // hint instead — `useReverification` detects this, opens the Clerk
  // modal, and on success retries the fetcher once. Any other action
  // outcome flows through unchanged.
  const enhanced = useReverification(
    async <T extends ActionResult>(
      action: () => Promise<T>,
    ): Promise<T | ReverificationHint> => {
      const result = await action();
      if (!result.ok && result.code === STEP_UP_ERROR_CODE) {
        return REVERIFICATION_HINT;
      }
      return result;
    },
  );

  const requireStepUp = useCallback(
    async <T extends ActionResult>(
      action: () => Promise<T>,
    ): Promise<T | undefined> => {
      try {
        // Clerk's typing strips ClerkError variants from the fetcher's
        // return type, so this is the original action result whenever
        // the action succeeded (or failed for a non-step-up reason) and
        // also the retried result after a successful reverification.
        return (await enhanced(action)) as T;
      } catch (e) {
        if (isReverificationCancelledError(e)) {
          throw new StepUpCancelledError();
        }
        throw e;
      }
    },
    [enhanced],
  );

  const runStepUp = useCallback(
    async <T>(
      mutate: () => Promise<T>,
      fallbackMessage: string,
    ): Promise<StepUpOutcome<T>> => {
      type Action = { ok: true; data: T } | FailureResult;
      try {
        const result = await requireStepUp<Action>(async () => {
          try {
            const data = await mutate();
            return { ok: true, data };
          } catch (e) {
            return failureFromError(e, fallbackMessage);
          }
        });
        if (result === undefined || !result.ok) {
          if (result !== undefined && result.code !== STEP_UP_ERROR_CODE) {
            return { status: 'error', code: result.code, message: result.message };
          }
          return { status: 'cancelled' };
        }
        return { status: 'ok', data: result.data };
      } catch (e) {
        if (e instanceof StepUpCancelledError) return { status: 'cancelled' };
        throw e;
      }
    },
    [requireStepUp],
  );

  return { requireStepUp, runStepUp };
}
